import React, { useState } from 'react'
import { Euro, Calculator, CheckCircle, AlertCircle, TrendingDown, Award, CreditCard, Clock, Users, Shield } from 'lucide-react'
import SEOHead from './SEOHead'

const PrixGreffeComparaisonPage = () => {
  const [selectedTechnique, setSelectedTechnique] = useState('saphori')
  const [greftCount, setGreftCount] = useState(2500)

  const techniquesPricing = {
    saphori: {
      name: "SAPHORÏ®",
      description: "Technique avancée avec densité optimisée",
      pricePerGraft: 2.2,
      minPrice: 2500,
      maxPrice: 6800,
      advantages: ["Densité jusqu'à 90 follicules/cm²", "Procédure rapide", "Excellent rapport qualité-prix", "Résultats naturels"],
      color: "#2F2F84"
    },
    choi: {
      name: "CHOÏ",
      description: "Précision millimétrique pour implantation directe",
      pricePerGraft: 2.5,
      minPrice: 2800,
      maxPrice: 7500,
      advantages: ["Implantation directe", "Contrôle angle parfait", "Minimal trauma", "Cicatrisation rapide"],
      color: "#6466B0"
    },
    saphir: {
      name: "SAPHIR",
      description: "FUE modernisée avec lames saphir",
      pricePerGraft: 2.0,
      minPrice: 2200,
      maxPrice: 6200,
      advantages: ["Incisions ultra-fines", "Cicatrisation optimale", "Technique éprouvée", "Prix accessible"],
      color: "#8B6F47"
    }
  }

  const competitorComparison = [
    {
      clinic: "CDGP - SAPHORÏ®",
      technique: "SAPHORÏ®",
      price: "2500€ - 6800€",
      pricePerGraft: "2,20€",
      maxDensity: "90 follicules/cm²",
      advantages: ["3 techniques disponibles", "Prix transparent", "Consultation gratuite"],
      location: "Paris 16e",
      color: "#2F2F84"
    },
    {
      clinic: "Maison Lutétia",
      technique: "DHI exclusif",
      price: "3900€ - 11900€*",
      pricePerGraft: "3,50€*",
      maxDensity: "Selon leurs standards",
      advantages: ["Technique spécialisée", "Réputation établie", "Formation universitaire"],
      location: "Paris 17e",
      color: "#8B4513"
    },
    {
      clinic: "Moyenne Paris",
      technique: "FUE Standard",
      price: "3000€ - 8000€*",
      pricePerGraft: "2,80€*",
      maxDensity: "Variable selon clinique",
      advantages: ["Technique classique", "Nombreux praticiens", "Prix variable"],
      location: "Paris",
      color: "#6B7280"
    }
  ]

  const financingOptions = [
    {
      name: "Paiement comptant",
      discount: "5% de remise",
      description: "Règlement intégral à la commande",
      icon: <Euro className="w-6 h-6" />
    },
    {
      name: "Paiement 4x sans frais",
      discount: "0% d'intérêt",
      description: "4 mensualités égales sur 4 mois",
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      name: "Paiement étalé 12 mois",
      discount: "TAEG 2,9%",
      description: "Mensualités adaptées à votre budget",
      icon: <Calculator className="w-6 h-6" />
    }
  ]

  const calculatePrice = (technique, grafts) => {
    const tech = techniquesPricing[technique]
    const basePrice = Math.max(grafts * tech.pricePerGraft, tech.minPrice)
    return Math.min(basePrice, tech.maxPrice)
  }

  const currentTechnique = techniquesPricing[selectedTechnique]
  const estimatedPrice = calculatePrice(selectedTechnique, greftCount)

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero overflow-hidden">
        <div className="container">
          <div className="hero-content">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 rounded-full" style={{ background: 'linear-gradient(135deg, var(--cdgp-gold) 0%, #8B6F47 100%)', boxShadow: 'var(--shadow)' }}>
                <Euro className="w-8 h-8 text-white" />
              </div>
              <div className="btn btn-gold">
                PRIX TRANSPARENTS 2025
              </div>
            </div>

            <h1>
              Prix Greffe de Cheveux Paris 2025
            </h1>

            <p>
              <strong>Comparatif transparent des prix</strong> entre CDGP et la concurrence parisienne.
              Découvrez pourquoi nous offrons le meilleur rapport qualité-prix avec nos 3 techniques avancées.
            </p>

            <div className="card" style={{ maxWidth: '32rem', margin: '0 auto' }}>
              <div className="flex items-center justify-center gap-4 mb-4">
                <TrendingDown className="w-8 h-8 text-green-500" />
                <h3 className="section-title" style={{ marginBottom: '0', fontSize: '1.5rem' }}>Prix CDGP vs Concurrence</h3>
              </div>
              <p style={{ fontSize: '1.1rem', fontWeight: '600', lineHeight: '1.6' }}>
                <span className="text-green-600 font-bold">Tarifs avantageux</span> selon nos analyses comparatives,
                <span style={{ color: 'var(--cdgp-primary)', fontWeight: 'bold' }}> résultats de qualité</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Price Calculator */}
      <section className="section" style={{ background: 'linear-gradient(135deg, var(--white) 0%, var(--cdgp-light) 100%)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Calculateur de Prix Personnalisé
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 auto', fontSize: '1.1rem' }}>
              Obtenez une estimation précise selon votre technique et nombre de greffons
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="card">
              {/* Technique Selection */}
              <div className="mb-8">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--cdgp-primary)', marginBottom: '1.5rem' }} className="flex items-center gap-3">
                  <div className="w-8 h-8 text-white rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)' }}>1</div>
                  Choisissez votre technique
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(techniquesPricing).map(([key, technique]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedTechnique(key)}
                      className={`card text-left transition-all duration-300 ${selectedTechnique === key
                        ? 'transform scale-105'
                        : 'hover:transform hover:scale-102'
                        }`}
                      style={{
                        borderColor: selectedTechnique === key ? technique.color : 'var(--border-gray)',
                        background: selectedTechnique === key ? `linear-gradient(135deg, ${technique.color}0D, ${technique.color}1A)` : 'white'
                      }}
                    >
                      <h4 style={{
                        fontWeight: 'bold',
                        fontSize: '1.125rem',
                        marginBottom: '0.5rem',
                        color: selectedTechnique === key ? technique.color : 'var(--text-primary)'
                      }}>
                        {technique.name}
                      </h4>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '0.75rem', lineHeight: '1.6' }}>
                        {technique.description}
                      </p>
                      <div className="section-title" style={{ fontSize: '1.125rem', marginBottom: '0' }}>
                        {technique.minPrice}€ - {technique.maxPrice}€
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Graft Count Slider */}
              <div className="mb-8">
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--cdgp-primary)', marginBottom: '1.5rem' }} className="flex items-center gap-3">
                  <div className="w-8 h-8 text-white rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)' }}>2</div>
                  Nombre de greffons estimé
                </h3>
                <div style={{ background: 'linear-gradient(135deg, var(--cdgp-light) 0%, #F9FAFB 100%)', padding: '2rem', borderRadius: '0.75rem', border: '1px solid var(--border-gray)', boxShadow: 'inset var(--shadow)' }}>
                  <div className="flex items-center justify-between mb-6">
                    <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Nombre de greffons</span>
                    <span className="section-title" style={{ fontSize: '1.875rem', marginBottom: '0' }}>{greftCount}</span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="4000"
                    step="100"
                    value={greftCount}
                    onChange={(e) => setGreftCount(parseInt(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider shadow-inner"
                    style={{
                      background: `linear-gradient(to right, ${currentTechnique.color} 0%, ${currentTechnique.color} ${((greftCount - 1000) / 3000) * 100}%, #e5e7eb ${((greftCount - 1000) / 3000) * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-3 font-medium">
                    <span>1000</span>
                    <span>2000</span>
                    <span>3000</span>
                    <span>4000</span>
                  </div>
                </div>
              </div>

              {/* Price Result */}
              <div style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)', color: 'white', padding: '2rem', borderRadius: '1rem', boxShadow: 'var(--shadow-hover)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">Prix Estimé - {currentTechnique.name}</h3>
                  <div className="text-6xl font-bold mb-4 text-shadow">{estimatedPrice.toLocaleString()}€</div>
                  <p className="text-lg opacity-90 mb-8 bg-white/10 px-4 py-2 rounded-full inline-block">
                    Pour {greftCount} greffons • {(estimatedPrice / greftCount).toFixed(2)}€ par greffon
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1.5rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <h4 className="font-bold mb-4 text-lg">Avantages {currentTechnique.name}</h4>
                      <ul className="text-sm space-y-2">
                        {currentTechnique.advantages.map((advantage, index) => (
                          <li key={index} className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                            <span>{advantage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1.5rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
                      <h4 className="font-bold mb-4 text-lg">Inclus dans le prix</h4>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                          <span>Consultation pré-opératoire</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                          <span>Intervention complète</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                          <span>Suivi post-opératoire 12 mois</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                          <span>Kit de soins inclus</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitor Comparison */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #F9FAFB 0%, var(--cdgp-light) 100%)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Comparatif des Prix à Paris
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 auto 2rem auto', fontSize: '1.1rem' }}>
              Analyse objective des tarifs pratiqués par les principales cliniques de greffe capillaire à Paris
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid gap-6">
              {competitorComparison.map((competitor, index) => (
                <div key={index} className={`card transition-all duration-300 ${index === 0 ? 'transform scale-105' : ''}`} style={{
                  borderLeft: index === 0 ? '4px solid var(--cdgp-primary)' : '4px solid var(--border-gray)',
                  background: index === 0 ? 'linear-gradient(90deg, rgba(47, 47, 132, 0.05) 0%, white 100%)' : 'white'
                }}>
                  <div className="grid md:grid-cols-6 gap-4 items-center">
                    <div className="md:col-span-2">
                      <h3 style={{
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        marginBottom: '0.5rem',
                        color: index === 0 ? 'var(--cdgp-primary)' : 'var(--text-primary)'
                      }}>
                        {competitor.clinic}
                        {index === 0 && (
                          <div className="btn btn-gold inline-block ml-2 text-xs" style={{ padding: '0.25rem 0.75rem' }}>
                            RECOMMANDÉ
                          </div>
                        )}
                      </h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>{competitor.location}</p>
                      <p style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>{competitor.technique}</p>
                    </div>

                    <div className="text-center">
                      <div className="section-title" style={{ fontSize: '1.5rem', marginBottom: '0' }}>{competitor.price}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{competitor.pricePerGraft}/greffon</div>
                    </div>

                    <div className="text-center">
                      <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>{competitor.maxDensity}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>Densité max</div>
                    </div>

                    <div className="md:col-span-2">
                      <ul className="space-y-2">
                        {competitor.advantages.map((advantage, advIndex) => (
                          <li key={advIndex} className="flex items-center gap-3 text-sm">
                            <CheckCircle className={`w-5 h-5 ${index === 0 ? 'text-green-500' : 'text-gray-400'}`} />
                            <span className="font-medium">{advantage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card mt-8" style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)', border: '2px solid #BBF7D0', boxShadow: 'var(--shadow)' }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', boxShadow: 'var(--shadow)' }}>
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#166534' }}>L'Avantage CDGP</h3>
              </div>
              <div className="disclaimer mb-4">
                <p className="disclaimer-text" style={{ fontSize: '0.875rem', color: '#059669' }}>
                  * Prix concurrents indicatifs basés sur informations publiques disponibles, susceptibles de variation.
                </p>
              </div>
              <p style={{ color: '#15803D', fontSize: '1.125rem', lineHeight: '1.6' }}>
                <strong>Avantage tarifaire significatif</strong> selon nos analyses comparatives,
                avec des résultats de qualité grâce à nos 3 techniques spécialisées et approche personnalisée.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="section" style={{ background: 'linear-gradient(135deg, var(--white) 0%, var(--cdgp-light) 100%)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Options de Financement
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 auto 2rem auto', fontSize: '1.1rem' }}>
              Nous rendons votre greffe capillaire accessible avec des solutions de paiement flexibles
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {financingOptions.map((option, index) => (
              <div key={index} className="service-card">
                <div className="text-center">
                  <div className="w-16 h-16 text-white rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)', boxShadow: 'var(--shadow)' }}>
                    {option.icon}
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--cdgp-primary)', marginBottom: '0.75rem' }}>
                    {option.name}
                  </h3>
                  <div style={{ fontSize: '1.125rem', fontWeight: 'bold', color: 'var(--cdgp-gold)', marginBottom: '1rem', background: 'linear-gradient(90deg, rgba(184, 155, 114, 0.1) 0%, transparent 100%)', padding: '0.25rem 0.75rem', borderRadius: '1rem' }}>
                    {option.discount}
                  </div>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {option.description}
                  </p>

                  {/* Decorative element */}
                  <div className="mt-6 w-full h-1 rounded-full opacity-30" style={{ background: 'linear-gradient(90deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 50%, var(--cdgp-gold) 100%)' }}></div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <div className="card text-center text-white" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)', boxShadow: 'var(--shadow-hover)', border: '1px solid rgba(255,255,255,0.2)' }}>
              <h3 className="text-2xl font-bold mb-6">Exemple de Financement</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--cdgp-gold)', marginBottom: '0.5rem' }}>4500€</div>
                  <div className="text-sm opacity-90 font-medium">Prix total</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--cdgp-gold)', marginBottom: '0.5rem' }}>375€</div>
                  <div className="text-sm opacity-90 font-medium">Par mois sur 12 mois</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--cdgp-gold)', marginBottom: '0.5rem' }}>2,9%</div>
                  <div className="text-sm opacity-90 font-medium">TAEG</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ben's Pricing Insights */}
      <section className="section" style={{ background: 'linear-gradient(135deg, var(--cdgp-light) 0%, #F0F2FF 100%)' }}>
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="card" style={{ border: '2px solid rgba(47, 47, 132, 0.2)' }}>
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div className="text-center lg:text-left">
                  <div className="w-32 h-32 mx-auto lg:mx-0 mb-6 rounded-2xl overflow-hidden border-4 shadow-lg" style={{ borderColor: 'var(--cdgp-primary)' }}>
                    <img
                      src="/assets/team/ben.jpg"
                      alt="Ben - Assistant IA CDGP"
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center text-white font-bold text-2xl" style={{ display: 'none', background: 'var(--cdgp-primary)' }}>
                      B
                    </div>
                  </div>
                  <div className="btn btn-gold inline-block mb-4" style={{ fontSize: '0.875rem' }}>
                    Ben, Assistant IA CDGP
                  </div>
                  <div style={{ background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)', padding: '1.5rem', borderRadius: '0.75rem', border: '2px solid #BBF7D0', boxShadow: 'var(--shadow)' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669', marginBottom: '0.25rem' }}>Économie Moyenne</div>
                    <div style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#047857', marginBottom: '0.25rem' }}>2000€ - 5000€</div>
                    <div style={{ fontSize: '0.875rem', color: '#059669', fontWeight: '500' }}>vs Maison Lutétia</div>
                  </div>
                </div>

                <div>
                  <h2 className="section-title" style={{ fontSize: '1.875rem', marginBottom: '1.5rem' }}>
                    Mon Analyse des Prix Parisiens
                  </h2>

                  <blockquote className="text-lg mb-6 leading-relaxed border-l-4 pl-6 p-4 rounded-r-lg" style={{ color: 'var(--text-secondary)', borderColor: 'var(--cdgp-gold)', background: 'linear-gradient(90deg, rgba(232, 234, 246, 0.3) 0%, transparent 100%)' }}>
                    "Selon mes analyses de <strong>nombreux devis parisiens</strong>, CDGP propose des tarifs
                    très compétitifs sur le marché. Nos 3 techniques permettent d'adapter la solution à votre budget
                    et votre profil. Nous optimisons notre organisation pour proposer
                    un excellent rapport qualité-prix avec transparence totale."
                  </blockquote>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between p-4 rounded-xl border-l-4 shadow-md" style={{ background: 'linear-gradient(90deg, #F0FDF4 0%, #DCFCE7 100%)', borderColor: '#10B981' }}>
                      <span style={{ fontWeight: '600', color: '#166534' }}>CDGP - SAPHORÏ®</span>
                      <span style={{ fontWeight: 'bold', color: '#047857', fontSize: '1.125rem' }}>2500€ - 6800€</span>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-xl border-l-4 shadow-md" style={{ background: 'linear-gradient(90deg, #FEF3C7 0%, #FDE68A 100%)', borderColor: '#F59E0B' }}>
                      <span style={{ fontWeight: '600', color: '#92400E' }}>Lutétia - DHI*</span>
                      <span style={{ fontWeight: 'bold', color: '#A16207', fontSize: '1.125rem' }}>3900€ - 11900€*</span>
                    </div>
                  </div>

                  <p className="italic p-3 rounded-lg" style={{ color: 'var(--text-secondary)', background: 'linear-gradient(90deg, rgba(232, 234, 246, 0.2) 0%, transparent 100%)' }}>
                    💰 Utilisez le calculateur ci-dessus pour obtenir votre estimation personnalisée instantanément !
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #F9FAFB 0%, var(--cdgp-light) 100%)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Questions Fréquentes sur les Prix
            </h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '32rem', margin: '0 auto', fontSize: '1.1rem' }}>
              Toutes les réponses à vos questions sur nos tarifs transparents
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Comment CDGP propose-t-il des tarifs avantageux ?",
                answer: "Nous optimisons notre organisation sans compromis sur la qualité. Notre approche multi-techniques (SAPHORÏ®, CHOÏ, SAPHIR) nous permet de proposer la solution la plus adaptée à votre profil et budget, avec transparence totale sur nos tarifs."
              },
              {
                question: "Le prix inclut-il le suivi post-opératoire ?",
                answer: "Oui, tous nos prix incluent : consultation pré-opératoire, intervention complète, suivi médical pendant 12 mois, kit de soins, et disponibilité 7j/7 via Ben notre assistant IA spécialisé."
              },
              {
                question: "Y a-t-il des frais cachés ?",
                answer: "Aucun frais caché. Le prix annoncé est le prix final. Contrairement à certains concurrents, nous n'ajoutons pas de suppléments pour l'anesthésie, les soins ou le suivi."
              },
              {
                question: "Puis-je payer en plusieurs fois ?",
                answer: "Oui, nous proposons le paiement 4x sans frais, ou un étalement sur 12 mois à 2,9% TAEG. Nous travaillons avec des organismes de crédit partenaires pour faciliter votre projet."
              }
            ].map((faq, index) => (
              <div key={index} className="card">
                <h3 className="flex items-start gap-3" style={{ fontWeight: 'bold', fontSize: '1.125rem', color: 'var(--cdgp-primary)', marginBottom: '1rem' }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)' }}>
                    {index + 1}
                  </div>
                  {faq.question}
                </h3>
                <p className="ml-9" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section text-white" style={{ background: 'linear-gradient(135deg, var(--cdgp-primary) 0%, var(--cdgp-medium) 100%)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
              Obtenez Votre Devis Personnalisé
            </h2>

            <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: '0.9', lineHeight: '1.6' }}>
              Consultation gratuite avec analyse capillaire et devis détaillé pour votre cas spécifique
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="https://www.doctolib.fr/centre-laser-et-esthetique/paris/clinique-du-grand-paris"
                target="_blank"
                rel="noopener noreferrer"
                className="btn"
                style={{ background: 'white', color: 'var(--cdgp-primary)', padding: '1rem 2rem', fontSize: '1.125rem' }}
              >
                Consultation Gratuite
              </a>

              <a
                href="/saphori-vs-dhi-paris"
                className="btn-secondary"
                style={{ border: '2px solid white', color: 'white', padding: '1rem 2rem', fontSize: '1.125rem' }}
              >
                Comparer les Techniques
              </a>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mt-12">
              {[
                { icon: <Shield className="w-6 h-6" />, text: "Prix garantis" },
                { icon: <Users className="w-6 h-6" />, text: "2000+ patients satisfaits" },
                { icon: <Clock className="w-6 h-6" />, text: "Suivi 12 mois inclus" },
                { icon: <Award className="w-6 h-6" />, text: "Techniques certifiées" }
              ].map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,255,255,0.2)', boxShadow: 'var(--shadow)' }}>
                    {feature.icon}
                  </div>
                  <p style={{ fontSize: '0.875rem', opacity: '0.9', fontWeight: '500' }}>{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SEO Schema */}
      <SEOHead
        title="Prix Greffe de Cheveux Paris 2025 : Comparatif Transparent"
        description="Combien coûte vraiment une greffe de cheveux à Paris ? Comparez les prix CDGP vs Lutétia. Devis immédiat et calculateur en ligne."
        schema={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "name": "Comparatif Prix Greffe Cheveux Paris",
          "description": "Analyse détaillée des tarifs de greffe capillaire à Paris.",
          "provider": {
            "@type": "MedicalOrganization",
            "name": "Clinique du Grand Paris",
            "url": "https://bonjour.cliniquedugrandparis.com/"
          },
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "2500",
            "highPrice": "6800",
            "priceCurrency": "EUR",
            "offerCount": "3"
          }
        }}
      />
    </div>
  )
}

export default PrixGreffeComparaisonPage